import React, { useEffect, useState } from "react";
import { Loader, Merge, X } from "lucide-react";
import { toast } from "react-toastify";
import { mergeOrders } from "../../api/OrderAPIs";

const MergeOrdersModal = ({ sourceOrder, onClose, onMerged }) => {
  const [otherOrderId, setOtherOrderId] = useState("");
  const [totalAmount, setTotalAmount] = useState(sourceOrder?.total ?? "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setOtherOrderId("");
    setTotalAmount(sourceOrder?.total ?? "");
  }, [sourceOrder]);

  if (!sourceOrder) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;

    const otherId = Number(otherOrderId);
    const total = Number(totalAmount);

    if (!otherId || otherId <= 0) {
      toast.error("حط رقم الأوردر التاني اللي هتدمجه");
      return;
    }
    if (otherId === Number(sourceOrder.orderNumber)) {
      toast.error("لا يمكن دمج الأوردر مع نفسه");
      return;
    }
    if (!Number.isFinite(total) || total <= 0) {
      toast.error("السعر الإجمالي بعد الدمج لازم يكون أكبر من صفر");
      return;
    }

    const confirmed = window.confirm(
      `تأكيد دمج الأوردر #${otherId} مع #${sourceOrder.orderNumber}؟\nالإجمالي بعد الدمج: ${total} ج.م\nالأوردر #${otherId} هيتحول لقديم ولن يُحذف.`
    );
    if (!confirmed) return;

    setSaving(true);
    try {
      const updated = await mergeOrders(sourceOrder.orderNumber, otherId, total);
      toast.success(`تم دمج الأوردر #${otherId} مع #${sourceOrder.orderNumber}`);
      onMerged?.(updated, otherId);
      onClose();
    } catch (err) {
      toast.error(err.message || "فشل دمج الطلبات");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4"
      dir="rtl"
      onClick={(e) => e.target === e.currentTarget && !saving && onClose()}
    >
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b bg-gradient-to-l from-teal-50 to-white">
          <div className="flex items-center gap-2">
            <Merge className="text-teal-700" size={22} />
            <div>
              <h2 className="text-lg font-bold text-teal-800">دمج الطلبات</h2>
              <p className="text-sm text-gray-500">الأوردر الأصلي #{sourceOrder.orderNumber}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="p-2 rounded-xl hover:bg-gray-100 disabled:opacity-50"
          >
            <X size={22} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <label className="block">
            <span className="block mb-1 font-medium text-gray-700">رقم الأوردر التاني</span>
            <input
              type="number"
              min="1"
              required
              autoFocus
              value={otherOrderId}
              onChange={(e) => setOtherOrderId(e.target.value)}
              className="w-full border-2 border-gray-200 rounded-xl px-3 py-3 focus:outline-none focus:ring-4 focus:ring-teal-200 focus:border-teal-400"
              placeholder="حط رقم الأوردر اللي هيتدمج"
            />
          </label>

          <label className="block">
            <span className="block mb-1 font-medium text-gray-700">السعر الإجمالي بعد الدمج</span>
            <input
              type="number"
              min="1"
              step="0.01"
              required
              value={totalAmount}
              onChange={(e) => setTotalAmount(e.target.value)}
              className="w-full border-2 border-gray-200 rounded-xl px-3 py-3 text-xl font-bold focus:outline-none focus:ring-4 focus:ring-teal-200 focus:border-teal-400"
              placeholder="السعر بعد الدمج"
            />
          </label>

          <p className="text-sm text-gray-500 leading-relaxed">
            القطع هتتضاف على الأوردر الأصلي، والأوردر التاني هيتحول لقديم مع ملاحظة الدمج.
          </p>
        </div>

        <div className="flex gap-3 px-5 py-4 border-t bg-gray-50">
          <button
            type="submit"
            disabled={saving}
            className="flex-1 bg-teal-600 hover:bg-teal-700 text-white px-4 py-3 rounded-xl font-bold disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving ? (
              <>
                <Loader className="animate-spin" size={18} />
                جاري الدمج...
              </>
            ) : (
              <>
                <Merge size={18} />
                تأكيد الدمج
              </>
            )}
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-3 rounded-xl border-2 border-gray-200 font-medium hover:bg-white disabled:opacity-50"
          >
            إلغاء
          </button>
        </div>
      </form>
    </div>
  );
};

export default MergeOrdersModal;
